import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { OrgMemberRole } from "@ceo-cmd/shared";
import {
  createOrg,
  getOrgWorkspace,
  inviteOrgMember,
  removeOrgMember,
  updateOrgMemberRole,
} from "@/api/org";
import {
  SettingsPageHeader,
  SettingsSection,
} from "@/components/settings/settings-ui";

const INVITE_ROLES: OrgMemberRole[] = ["admin", "member", "viewer"];

function RoleBadge({ role }: { role: string }) {
  return (
    <span className="inline-flex rounded-md border border-border bg-muted/40 px-2 py-0.5 text-xs font-medium capitalize text-foreground">
      {role}
    </span>
  );
}

export function SettingsWorkspacePage() {
  const { pathname } = useLocation();
  const isUsers = pathname.includes("/settings/users");
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState<OrgMemberRole>("member");

  const { data, isLoading, error } = useQuery({
    queryKey: ["org"],
    queryFn: getOrgWorkspace,
    staleTime: 30_000,
  });

  const org = data?.org ?? null;
  const role = data?.role ?? null;
  const members = data?.members ?? [];
  const canManage = role === "owner" || role === "admin";

  const createMutation = useMutation({
    mutationFn: () => createOrg(name.trim()),
    onSuccess: () => {
      setName("");
      void qc.invalidateQueries({ queryKey: ["org"] });
      void qc.invalidateQueries({ queryKey: ["profile"] });
    },
  });

  const inviteMutation = useMutation({
    mutationFn: () => inviteOrgMember(inviteEmail.trim(), inviteRole),
    onSuccess: () => {
      setInviteEmail("");
      void qc.invalidateQueries({ queryKey: ["org"] });
    },
  });

  const roleMutation = useMutation({
    mutationFn: ({
      userId,
      nextRole,
    }: {
      userId: string;
      nextRole: OrgMemberRole;
    }) => updateOrgMemberRole(userId, nextRole),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org"] }),
  });

  const removeMutation = useMutation({
    mutationFn: (userId: string) => removeOrgMember(userId),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["org"] }),
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
        <p className="text-sm text-destructive">Failed to load workspace.</p>
      </div>
    );
  }

  if (!isUsers && org) {
    return (
      <div>
        <SettingsPageHeader
          title="Workspace overview"
          description="Workspace details and teammates for CEO Command."
        />
        <SettingsSection title="Workspace details">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="flex size-12 items-center justify-center rounded-lg bg-brand/15 text-lg font-bold text-brand">
                {org.name.slice(0, 2).toUpperCase()}
              </div>
              <div>
                <p className="text-base font-semibold text-foreground">
                  {org.name}
                </p>
                <p className="text-xs text-muted-foreground">
                  Org ID · {org.id}
                </p>
              </div>
            </div>
            {role && <RoleBadge role={role} />}
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-border px-3 py-3">
              <p className="text-[11px] uppercase text-muted-foreground">
                Users
              </p>
              <p className="mt-1 text-lg font-semibold">{members.length}</p>
            </div>
            <div className="rounded-md border border-border px-3 py-3">
              <p className="text-[11px] uppercase text-muted-foreground">
                Created
              </p>
              <p className="mt-1 text-sm font-medium">
                {new Date(org.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3 text-sm">
            <Link to="/settings/users" className="text-brand hover:underline">
              Manage users
            </Link>
            <Link to="/settings/ai" className="text-brand hover:underline">
              AI providers
            </Link>
          </div>
        </SettingsSection>
      </div>
    );
  }

  return (
    <div className="max-w-2xl space-y-8">
      <SettingsPageHeader
        title={isUsers ? "Users" : "Workspace overview"}
        description={
          isUsers
            ? "Invite teammates and manage roles for CEO Command."
            : "Create or manage your CEO Command workspace."
        }
      />

      {org ? (
        <>
          {!isUsers && (
            <div className="space-y-3 rounded-lg border border-border bg-card p-5">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs uppercase tracking-wide text-muted-foreground">
                    Name
                  </p>
                  <p className="mt-0.5 text-sm font-medium text-foreground">
                    {org.name}
                  </p>
                </div>
                {role && <RoleBadge role={role} />}
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">
                  Org ID
                </p>
                <p className="mt-0.5 font-mono text-sm text-foreground">
                  {org.id}
                </p>
              </div>
            </div>
          )}

          <section className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">Members</h3>
              <span className="text-xs text-muted-foreground">
                {members.length} total
              </span>
            </div>

            <ul className="divide-y divide-border rounded-lg border border-border">
              {members.map((m) => (
                <li
                  key={m.userId}
                  className="flex flex-wrap items-center justify-between gap-2 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm text-foreground">
                      {m.email || m.userId}
                    </p>
                    <p className="text-xs capitalize text-muted-foreground">
                      {m.status}
                      {m.userId.startsWith("invite:")
                        ? " · pending signup"
                        : ""}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    {canManage && m.role !== "owner" ? (
                      <select
                        value={m.role}
                        onChange={(e) =>
                          roleMutation.mutate({
                            userId: m.userId,
                            nextRole: e.target.value as OrgMemberRole,
                          })
                        }
                        className="rounded-md border border-border bg-bg px-2 py-1 text-xs text-foreground"
                      >
                        {INVITE_ROLES.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                        {role === "owner" && (
                          <option value="owner">owner (transfer)</option>
                        )}
                      </select>
                    ) : (
                      <RoleBadge role={m.role} />
                    )}
                    {canManage && m.role !== "owner" && (
                      <button
                        type="button"
                        onClick={() => removeMutation.mutate(m.userId)}
                        className="text-xs text-destructive hover:underline"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                </li>
              ))}
            </ul>

            {role === "owner" && (
              <p className="text-xs text-muted-foreground">
                To transfer ownership, set another member&apos;s role to owner —
                you will become an admin.
              </p>
            )}
          </section>

          {canManage && (
            <section className="space-y-3 rounded-lg border border-border bg-card p-5">
              <h3 className="text-sm font-semibold text-foreground">
                Invite member
              </h3>
              <div className="flex flex-wrap gap-2">
                <input
                  type="email"
                  placeholder="email@company.com"
                  value={inviteEmail}
                  onChange={(e) => setInviteEmail(e.target.value)}
                  className="min-w-[200px] flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
                />
                <select
                  value={inviteRole}
                  onChange={(e) =>
                    setInviteRole(e.target.value as OrgMemberRole)
                  }
                  className="rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground"
                >
                  {INVITE_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {r}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  disabled={!inviteEmail.trim() || inviteMutation.isPending}
                  onClick={() => inviteMutation.mutate()}
                  className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
                >
                  {inviteMutation.isPending ? "Inviting…" : "Invite"}
                </button>
              </div>
              {inviteMutation.isError && (
                <p className="text-sm text-destructive">
                  {inviteMutation.error instanceof Error
                    ? inviteMutation.error.message
                    : "Failed to invite"}
                </p>
              )}
              {inviteMutation.isSuccess && (
                <p className="text-sm text-muted-foreground">
                  Invite sent. If no account matches that email yet, a pending
                  stub was created.
                </p>
              )}
            </section>
          )}
        </>
      ) : (
        <div className="space-y-4 rounded-lg border border-border bg-card p-5">
          <p className="text-sm text-muted-foreground">
            You are not in a workspace yet. Create one to invite teammates to
            CEO Command.
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              placeholder="Workspace name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="flex-1 rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand"
            />
            <button
              type="button"
              disabled={!name.trim() || createMutation.isPending}
              onClick={() => createMutation.mutate()}
              className="rounded-md bg-brand px-4 py-2 text-sm font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              {createMutation.isPending ? "Creating…" : "Create"}
            </button>
          </div>
          {createMutation.isError && (
            <p className="text-sm text-destructive">
              {createMutation.error instanceof Error
                ? createMutation.error.message
                : "Failed to create workspace"}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
